import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useToastController} from '@vio/atlas/context/toastController'

import {useRouterProvider} from '@daedalus/core/src/_web/router/context/RouterContext'
import {trackEvent} from '@daedalus/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@daedalus/core/src/analytics/types/Events'
import {
  ParametrizedHotelContext,
  SearchContext
} from '@daedalus/core/src/analytics/types/Events'

import {clearToast} from '../../slice'
import {selectToast} from '../../slice/selectors'
import {
  PriceWatchToastIcon,
  PriceWatchToastMessage
} from '../PriceWatchToastMessage'

interface PriceWatchToastListenerProps {
  getSearchContext: (
    state: Record<string, string | number>,
    searchIdParam?: string
  ) => SearchContext
  getParametrizedHotelContext: (hotelId: string) => ParametrizedHotelContext
}

/**
 * Global toast listener for price watch feedback.
 * Displays toasts regardless of component lifecycle,
 * solving the virtual list unmounting issue.
 */
export const PriceWatchToastListener: React.FC<
  PriceWatchToastListenerProps
> = ({getSearchContext, getParametrizedHotelContext}) => {
  const dispatch = useDispatch()
  const toastData = useSelector(selectToast)
  const {open, closeAll} = useToastController()
  const {push} = useRouterProvider()

  // Get contexts using the passed selectors
  const searchContext = useSelector(getSearchContext)

  useEffect(() => {
    if (!toastData) return

    const {
      hotel,
      checkIn,
      checkOut,
      enabled,
      shouldShowNavigationIcon,
      alertsManagementUrl
    } = toastData

    const handleToastClick = () => {
      closeAll()

      // Track analytics using selectors to get current context
      const hotelContext = getParametrizedHotelContext(hotel.objectID)

      dispatch(
        trackEvent({
          category: Category.User,
          entity: Entity.PriceAlertToast,
          action: Action.Clicked,
          team: Team.Retention,
          analyticsContext: {
            [AnalyticsContext.SearchContext]: searchContext,
            [AnalyticsContext.HotelContext]: hotelContext
          }
        })
      )

      // Navigate to alerts management if URL is provided
      if (alertsManagementUrl) {
        push(alertsManagementUrl)
      }
    }

    // Show the toast
    open(
      <PriceWatchToastMessage
        hotel={hotel}
        checkIn={checkIn}
        checkOut={checkOut}
        shouldShowNavigationIcon={shouldShowNavigationIcon}
        enabled={enabled}
      />,
      {
        icon: <PriceWatchToastIcon enabled={enabled} />,
        onClick: handleToastClick
      }
    )

    // Immediately clear the toast from state to prevent replay
    dispatch(clearToast())
  }, [
    toastData,
    open,
    closeAll,
    push,
    dispatch,
    searchContext,
    getParametrizedHotelContext
  ])

  // This component renders nothing visible
  return null
}
